import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  Loader2,
  Sparkles,
  ListChecks,
  FileText,
  StickyNote,
  ArrowLeft,
  Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail, formatDuration, relTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

export default function MeetingDetail({ meeting, onBack, onChanged, onTasksChanged, onDelete }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [checked, setChecked] = useState({});

  const posted = useMemo(() => new Set(meeting.posted_task_ids || []), [meeting]);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    setAudioUrl(null);
    api
      .get(`/meetings/${meeting.id}/audio`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setAudioUrl(url);
      })
      .catch((err) => console.error("Failed to load meeting audio:", err));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [meeting.id]);

  useEffect(() => {
    const next = {};
    (meeting.todos || []).forEach((t) => {
      next[t.id] = !posted.has(t.id);
    });
    setChecked(next);
  }, [meeting.id, meeting.todos, posted]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/meetings/${meeting.id}/analyze`);
      onChanged?.(r.data);
      toast.success("Notes and to-dos are ready");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI summary failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const postTasks = async () => {
    const ids = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) {
      toast.error("Tick at least one to-do first");
      return;
    }
    setPosting(true);
    try {
      const r = await api.post(`/meetings/${meeting.id}/tasks`, { todo_ids: ids });
      toast.success(`${r.data.created_tasks.length} task(s) posted to the Task Board`);
      onTasksChanged?.();
      const fresh = await api.get(`/meetings/${meeting.id}`);
      onChanged?.(fresh.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post tasks");
    } finally {
      setPosting(false);
    }
  };

  const download = () => {
    if (!audioUrl) return;
    const a = document.createElement("a");
    a.href = audioUrl;
    a.download = `${meeting.title.replace(/[^\w\-]+/g, "_")}.webm`;
    a.click();
  };

  const selectedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div className="flex-1 min-w-0 flex flex-col bg-[#16181F]" data-testid="meeting-detail">
      <div className="px-4 md:px-6 py-4 border-b border-[#2E3242] shrink-0">
        <div className="flex items-start gap-3">
          <button
            data-testid="meeting-back-button"
            onClick={onBack}
            className="lg:hidden text-gray-400 hover:text-white mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 data-testid="meeting-title" className="font-display font-bold text-lg text-gray-100 truncate">
              {meeting.title}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {meeting.recorded_by} · {relTime(meeting.created_at)}
              {meeting.duration ? ` · ${formatDuration(meeting.duration)}` : ""}
            </p>
          </div>
          <button
            data-testid="meeting-delete-button"
            onClick={() => onDelete(meeting.id)}
            className="text-gray-600 hover:text-red-400 mt-1"
            title="Remove from archive"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5">
        <div className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-4 mb-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500">Raw voice note</p>
            <button
              data-testid="meeting-download-button"
              onClick={download}
              disabled={!audioUrl}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-300 disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          </div>
          {audioUrl ? (
            <audio data-testid="meeting-audio-player" src={audioUrl} controls className="w-full h-10" />
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin text-red-400" /> Loading audio…
            </div>
          )}
        </div>

        <Tabs defaultValue="notes">
          <TabsList className="bg-[#1E2029] border border-[#2E3242] h-auto p-1 flex-wrap">
            <TabsTrigger
              value="notes"
              data-testid="meeting-tab-notes"
              className="data-[state=active]:bg-red-950/70 data-[state=active]:text-red-200 text-gray-400 text-xs sm:text-sm"
            >
              <StickyNote className="w-3.5 h-3.5 mr-1.5" /> AI Notes
            </TabsTrigger>
            <TabsTrigger
              value="todos"
              data-testid="meeting-tab-todos"
              className="data-[state=active]:bg-red-950/70 data-[state=active]:text-red-200 text-gray-400 text-xs sm:text-sm"
            >
              <ListChecks className="w-3.5 h-3.5 mr-1.5" /> To-dos
              {meeting.todos?.length > 0 && (
                <span className="ml-1.5 text-[10px] font-mono">{meeting.todos.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="transcript"
              data-testid="meeting-tab-transcript"
              className="data-[state=active]:bg-red-950/70 data-[state=active]:text-red-200 text-gray-400 text-xs sm:text-sm"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Transcript
            </TabsTrigger>
          </TabsList>

          <TabsContent value="notes" className="mt-4">
            {meeting.status !== "analyzed" ? (
              <div className="rounded-xl border border-red-900/40 bg-[#1E1013]/60 p-5 text-center">
                <p className="text-sm text-gray-400 mb-3">
                  This recording has a transcript but no AI notes yet.
                </p>
                <Button
                  data-testid="meeting-analyze-button"
                  onClick={analyze}
                  disabled={analyzing}
                  className="ruby-gradient text-white border border-red-900/40"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Writing notes…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" /> Generate notes & to-dos
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-5" data-testid="meeting-notes">
                {meeting.summary && (
                  <p className="text-sm text-gray-300 leading-relaxed">{meeting.summary}</p>
                )}
                {meeting.notes?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Key notes</p>
                    <ul className="space-y-1.5">
                      {meeting.notes.map((n, i) => (
                        <li key={i} className="text-sm text-gray-300 flex gap-2">
                          <span className="text-red-500 mt-0.5">•</span>
                          <span>{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {meeting.decisions?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-emerald-400 mb-2">Decisions</p>
                    <ul className="space-y-1.5">
                      {meeting.decisions.map((d, i) => (
                        <li key={i} className="text-sm text-gray-300 flex gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="todos" className="mt-4">
            {!meeting.todos?.length ? (
              <p className="text-sm text-gray-500">
                No to-dos yet — generate the AI notes first.
              </p>
            ) : (
              <div className="space-y-2" data-testid="meeting-todos">
                {meeting.todos.map((t) => {
                  const isPosted = posted.has(t.id);
                  return (
                    <label
                      key={t.id}
                      data-testid={`meeting-todo-${t.id}`}
                      className="flex items-start gap-3 rounded-xl border border-[#2E3242] bg-[#1E2029] p-3 cursor-pointer hover:border-red-900/40 transition-colors"
                    >
                      <Checkbox
                        data-testid={`meeting-todo-check-${t.id}`}
                        checked={!!checked[t.id]}
                        onCheckedChange={(v) => setChecked((c) => ({ ...c, [t.id]: !!v }))}
                        className="mt-0.5 border-[#2E3242] data-[state=checked]:bg-red-700 data-[state=checked]:border-red-700"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-200 leading-snug">{t.title}</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded border capitalize text-amber-300 bg-amber-950/40 border-amber-800/50">
                            {t.priority}
                          </span>
                          {t.owner && (
                            <span className="text-[11px] text-gray-500">for {t.owner}</span>
                          )}
                          {isPosted && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-950/50 text-emerald-300 border border-emerald-900/40">
                              already on board
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
                <Button
                  data-testid="post-selected-tasks-button"
                  onClick={postTasks}
                  disabled={posting || selectedCount === 0}
                  className="w-full mt-2 ruby-gradient text-white border border-red-900/40 h-11"
                >
                  {posting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Posting…
                    </>
                  ) : (
                    <>
                      <ListChecks className="w-4 h-4 mr-1.5" /> Post {selectedCount} selected task
                      {selectedCount === 1 ? "" : "s"}
                    </>
                  )}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="transcript" className="mt-4">
            <p
              data-testid="meeting-transcript"
              className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap break-words font-mono bg-[#1E2029] border border-[#2E3242] rounded-xl p-4"
            >
              {meeting.transcript}
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
